import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {PANE_MARGIN} from 'insights/InsightsConstants';
import {LEGACY_FONT_PROXIMA_15} from 'styles/FontConstants';
import {ellipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {
  DnDDragHandleArea,
  DnDDragHandleIcon,
  DnDDragHandleProps,
} from 'ui-library/drag-and-drop/DnDDragHandle';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Header2} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import PaneWarningsIcon from './PaneWarningsIcon';

export enum PaneBorder {
  BOX_SHADOW = 'BOX_SHADOW',
  SOLID = 'SOLID',
  NO_BORDER = 'NO_BORDER',
}

export enum HeaderBorder {
  SOLID = 'SOLID',
  NONE = 'NO_BORDER',
}

export interface PaneProps {
  banner?: ReactNode;
  centerContent?: boolean;
  children: ReactNode;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
  description?: string;
  dragHandleProps?: DnDDragHandleProps;
  fitContent?: boolean;
  headerBorder?: HeaderBorder;
  paneBorder?: PaneBorder;
  sidebar?: ReactNode;
  title: ReactNode;
  toolbar?: ReactNode;
  warnings?: Array<string>;
}

/**
 * Pane component that has a header, content, optional sidebar, and optional toolbar.
 *
 * @param centerContent Vertically and horizontally center the children
 * @param children Content of the Pane
 * @param description If specified, render an info icon to the right of the title with the description text in the tooltip
 * @param dragHandleProps If using Pane with DNDCanvas, pass the dragHandleProps through this
 * @param fitContent If the Pane's height should grow to fit its contents
 * @param paneBorder The kind of border to use around the pane
 * @param sidebar If specified, render a sidebar and render a ReactNode in it
 * @param title Title content for the header of Pane
 * @param toolbar Content to render to the right of the header in the Pane
 * @param warnings If specified, render a warning icon to the left of the toolbar with the warnings in a Popover
 * @param dataTestAttrs
 */
export default function Pane({
  centerContent,
  children,
  description,
  dragHandleProps,
  fitContent = false,
  headerBorder = HeaderBorder.SOLID,
  paneBorder = PaneBorder.BOX_SHADOW,
  sidebar,
  title,
  toolbar,
  warnings = [],
  banner,
  dataTestAttrs,
}: PaneProps): JSX.Element {
  return (
    <div
      className={classNames(
        'u-flex-full-height',
        css(paneStyles.pane),
        paneBorder === PaneBorder.SOLID && css(paneStyles.solidBorder),
        paneBorder === PaneBorder.BOX_SHADOW && css(paneStyles.boxShadowBorder),
        !fitContent && css(paneStyles.fitHeightPane)
      )}
      {...getTestProps(dataTestAttrs)}
    >
      <div
        className={css(
          paneStyles.header,
          headerBorder === HeaderBorder.SOLID && paneStyles.solidHeaderBorder
        )}
      >
        <div className={classNames('u-lr-split', 'u-vertical-center-items')}>
          <div className={css(paneStyles.headerTitle)}>
            <Header2 className={css(paneStyles.headerText)}>{title}</Header2>
            <span className="u-mini-spacing-right" />
            {description && (
              <Tooltip placement={FloatingPlacement.BOTTOM} text={description}>
                <BenchlingIcon
                  icon="info-circle"
                  className={classNames(
                    'u-mini-spacing-left u-mini-spacing-top',
                    css(paneStyles.descriptionIcon)
                  )}
                />
              </Tooltip>
            )}
          </div>
          {dragHandleProps && <DnDDragHandleArea {...dragHandleProps} />}
          <div className={classNames('u-vertical-center-items', css(paneStyles.headerToolbar))}>
            <div>
              <PaneWarningsIcon messages={warnings} />
              {toolbar}
            </div>
            {dragHandleProps && (
              <div className="u-mini-spacing-left">
                <DnDDragHandleIcon {...dragHandleProps} />
              </div>
            )}
          </div>
        </div>
        {banner && <div className={css(paneStyles.headerBanner)}>{banner}</div>}
      </div>
      <div className={css(paneStyles.contentContainer)}>
        <div
          className={classNames(
            css(paneStyles.content, centerContent && paneStyles.centeredContent),
            'scrollbar-override'
          )}
          {...getTestProps({component: 'Pane', element: 'content'})}
        >
          {children}
        </div>
        {sidebar != null && (
          <>
            <div className={css(paneStyles.sidebarSeparator)} />
            <div
              className={css(paneStyles.sidebarContainer)}
              {...getTestProps({component: 'Pane', element: 'sidebar'})}
            >
              {sidebar}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

Pane.defaultProps = {
  dataTestAttrs: {
    component: 'Pane',
    element: 'root',
  },
  isExpanded: false,
};

const PANE_SIDEBAR_WIDTH = 232;
const SOLID_BORDER_STYLE = `1px solid ${GRAY_LIGHT}`;
const BOX_SHADOW_BORDER_STYLE = '0px 0px 4px rgb(92, 124, 131, 20%)';

export const paneStyles = StyleSheet.create({
  solidBorder: {
    border: SOLID_BORDER_STYLE,
  },
  boxShadowBorder: {
    boxShadow: BOX_SHADOW_BORDER_STYLE,
  },
  contentContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
  },
  centeredContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexGrow: 1,
    overflowX: 'auto',
    overflowY: 'auto',
    flexDirection: 'column',
  },
  fitHeightPane: {
    height: '100%',
  },
  header: {
    paddingTop: PANE_MARGIN,
    paddingRight: PANE_MARGIN,
    paddingBottom: 8,
    paddingLeft: PANE_MARGIN,
  },
  headerBanner: {
    paddingTop: 8,
  },

  headerText: {
    ...ellipsisText,
    // Ensure title takes up the full line height, even if the header text is empty.
    minHeight: LEGACY_FONT_PROXIMA_15.lineHeight,
  },
  headerTitle: {
    display: 'flex',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  headerToolbar: {
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
  pane: {
    backgroundColor: WHITE,
    borderRadius: 4,
    overflow: 'hidden',
  },
  sidebarContainer: {
    flexBasis: PANE_SIDEBAR_WIDTH,
    flexGrow: 0,
    flexShrink: 0,
    height: '100%',
    overflowY: 'auto',
  },
  sidebarSeparator: {
    borderLeft: SOLID_BORDER_STYLE,
  },
  solidHeaderBorder: {
    borderBottom: SOLID_BORDER_STYLE,
  },
  descriptionIcon: {
    flexShrink: 0,
    transform: 'translateY(1px)',
    width: '14px',
    height: '14px',
    marginBottom: '0px',
  },
});
